import { TGetOrganizationListResDto, TOrganizationListReqDto } from "@/api/services/admin/organization/dtos/get-organization-list.dto";
import { TOrganizationContractStatus } from "@/types/enum";
import { TCreateOrganizationReqDto, TCreateOrganizationResDto } from "@/api/services/admin/organization/dtos/create-organization.dto";
import { TDeleteOrganizationResDto } from "@/api/services/admin/organization/dtos/delete-organization.dto";
import { TGetOrganizationContractStatusCountResDto } from "@/api/services/admin/organization/dtos/get-contract-status-count.dto";
import { TGetOrganizationResDto } from "@/api/services/admin/organization/dtos/get-organization.dto";
import { TUpdateOrganizationReqDto, TUpdateOrganizationResDto } from "@/api/services/admin/organization/dtos/update-organization.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { usePagination } from "@/hooks/usePagination";
import { clientApi } from "@/utils/api";
import { isNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

export const fetchOrganizationListApi = async (req: TOrganizationListReqDto): Promise<TGetOrganizationListResDto> => {
  const url = createUrlWithParams("/organizations", req);
  const { data } = await clientApi.get<TGetOrganizationListResDto>(url);
  return data;
};

export const fetchOrganizationApi = async (organizationId: string): Promise<TGetOrganizationResDto> => {
  const { data } = await clientApi.get<TGetOrganizationResDto>(`/organizations/${organizationId}`);
  return data;
};

export const createOrganizationApi = async (payload: TCreateOrganizationReqDto): Promise<TCreateOrganizationResDto> => {
  const { data } = await clientApi.post<TCreateOrganizationResDto>(`/organizations`, payload);
  return data;
};

export const updateOrganizationApi = async (organizationId: string, payload: TUpdateOrganizationReqDto): Promise<TUpdateOrganizationResDto> => {
  const { data } = await clientApi.put<TUpdateOrganizationResDto>(`/organizations/${organizationId}`, payload);
  return data;
};

export const deleteOrganizationApi = async (organizationId: string): Promise<TDeleteOrganizationResDto> => {
  const { data } = await clientApi.delete<TDeleteOrganizationResDto>(`/organizations/${organizationId}`);
  return data;
};

export const getOrganizationStatusCountApi = async (): Promise<TGetOrganizationContractStatusCountResDto> => {
  const { data } = await clientApi.get<TGetOrganizationContractStatusCountResDto>(`/organizations/get-contract-status-count`);
  return data;
};

export const useOrganizationList = (contractStatus?: TOrganizationContractStatus) => {
  const pagReq = usePagination();
  const params = { ...pagReq, contractStatuses: contractStatus ? [contractStatus] : undefined };
  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST, params],
    queryFn: () => fetchOrganizationListApi(params),
  });
};

export const useOrganizationStatusCount = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.GET_CONTRACT_STATUS_COUNT],
    queryFn: () => getOrganizationStatusCountApi(),
  });
};

export const useOrganization = () => {
  const { id } = useParams();
  return useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchOrganizationApi(id) : undefined),
    enabled: Boolean(id && isNumber(id)),
  });
};
